import {EventEmitter, inject, Injectable} from '@angular/core';
import {VaultStore} from '../../stores/vault.store';
import {VaultsService as ApiVaultsService} from '../../api/services/vaults.service';
import {UsersService as ApiUsersService} from '../../api/services/users.service';
import {SnackbarService} from '../snackbar/snackbar.service';
import {TagGroupDefinition, Vault} from 'src/app/api/models';
import {NavigationService} from '../navigation/navigation.service';
import {PermissionService} from '../permission/permission.service';
import {VaultQuery} from '../../queries/vault.query';
import {firstValueFrom, lastValueFrom} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {PermissionQuery} from '../../queries/permission.query';
import {TagGroupDefinitionWithIcon} from '../../models/tag/tag-group-definition-with-icon.model';
import {AppQuery} from '../../queries/app.query';
import {HttpContext, HttpErrorResponse} from '@angular/common/http';
import {LOCAL_FILE_SERVICE, LocalFileService} from '../local-file/local-file.service';
import {VaultService as BfaVaultService} from '../../bfa-api/services/vault.service';
import {VaultList} from '../../bfa-api/models/vault-list';
import {Vault as BfaVault} from '../../bfa-api/models/vault';
import {CheckoutEventService} from '../electron/checkout/checkout-event.service';
import {getLocalVaultNameFormat} from '../../util/local-aptera-vault-name-by-vault.util';
import {SystemInfoService} from '../system-info/system-info.service';
import {Icon} from '../../types/icons/icon.type';
import {ICONS} from '../../constants/icons/icons.constants';
import {defaultTagGroupDefinitions} from '../../constants/default-tag-group-definitions.constants';
import {AUTH_TOKEN_HTTP_CONTEXT} from '../../constants/auth-token-http-context.constant';
import {AuthService} from '../auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class VaultService {
    public vaultCreatedEvent: EventEmitter<void> = new EventEmitter();
    private readonly apiVaultsService = inject(ApiVaultsService);
    private readonly apiUsersService = inject(ApiUsersService);
    private readonly vaultStore = inject(VaultStore);
    private readonly snackbarService = inject(SnackbarService);
    private readonly navigationService = inject(NavigationService);
    private readonly permissionService = inject(PermissionService);
    private readonly permissionQuery = inject(PermissionQuery);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly appQuery = inject(AppQuery);
    private readonly localFileService = inject<LocalFileService>(LOCAL_FILE_SERVICE);
    private readonly checkoutEventService = inject(CheckoutEventService);
    private readonly bfaVaultService = inject(BfaVaultService);
    private readonly systemInfoService = inject(SystemInfoService);
    private readonly authService = inject(AuthService);

    public constructor() {
        const vaultId = this.vaultQuery.getFirstActiveId();
        if (vaultId) {
            this.authService.continueAfterLoggedIn(async () => {
                await this.permissionService.fetchVaultPermissions(vaultId);
                return true;
            });
        }
    }

    public async createVault(vaultName: string, classificationMode: 'None' | 'Manual' | 'Automatic' | 'SemiAutomatic' = 'SemiAutomatic'): Promise<boolean> {
        try {
            const vault = await firstValueFrom(this.apiVaultsService.VaultsCreateVault(
                {
                    vaultCreationData: {
                        name: vaultName,
                        classificationMode
                    },
                    Authorization: await this.authService.getBearer()
                }));

            await this.localFileService.createVaultDirectory(vault);
            this.vaultCreatedEvent.emit();

            return true;
        } catch (err) {
            this.snackbarService.showError('VAULTS_CREATION_ERROR_MSG', err as Error);
            return false;
        }
    }

    public async setActiveVault(vaultId: string): Promise<void> {
        this.vaultStore.setActive([vaultId]);
        await this.permissionService.fetchVaultPermissions(vaultId);
    }

    public async fetchUserVaults(userId: string): Promise<void> {
        this.setLoading(true);
        try {
            const vaults = await firstValueFrom(this.apiUsersService.UsersGetUserJointVaults({
                userId,
                Authorization: await this.authService.getBearer()
            }));
            this.vaultStore.update({ userVaults: vaults.map(v => v.id) });
        } catch (err) {
            this.snackbarService.showError('VAULTS_LOADING_ERROR_MSG', err as Error);
        } finally {
            this.setLoading(false);
        }
    }

    public async fetchAndGetVaultById(vaultId: string): Promise<Vault | undefined> {
        this.setLoading(true);
        let vault: Vault | undefined;
        try {
            vault = await lastValueFrom(this.apiVaultsService.VaultsGetVault({
                vaultId,
                Authorization: await this.authService.getBearer()
            }));
            this.vaultStore.upsertMany([vault]);
            const ids = this.vaultStore.getValue().ids;
            this.setHasDataLoaded(!!ids && ids.length > 0);
        } catch (err) {
            this.snackbarService.showError('ERROR.VAULT.FETCH_SINGLE', err as Error);
        } finally {
            this.setLoading(false);
        }
        return vault;
    }

    public async fetchVaultDetailView(vaultId: string): Promise<BfaVault> {
        const token = await this.authService.getToken();
        return lastValueFrom(this.bfaVaultService.vaultGetVault({ vaultId }, new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token)));
    }

    public async fetchAndUpsertVaults(): Promise<Array<Vault>> {
        this.setLoading(true);
        let result: Array<Vault> = [];
        try {
            const vaults = await firstValueFrom(this.apiVaultsService.VaultsGetVaults(await this.authService.getBearer()));
            if (this.systemInfoService.isElectron() && this.appQuery.getIsInitialized()) {
                const oldVaultIds = this.vaultStore.getValue().ids || [];
                for (const vault of vaults) {
                    if (oldVaultIds.includes(vault.id)) {
                        continue;
                    }
                    await this.localFileService.createVaultDirectory(vault);
                }
            }
            this.vaultStore.upsertMany(vaults);
            result = vaults;
            const ids = this.vaultStore.getValue().ids;
            this.setHasDataLoaded(!!ids && ids.length > 0);
        } catch (err) {
            this.snackbarService.showError('VAULTS_LOADING_ERROR_MSG', err as Error);
        } finally {
            this.setLoading(false);
        }
        return result;
    }

    public async fetchVaultsIfEmpty(forceReload: boolean = false): Promise<void> {
        if (!forceReload && Object.keys(this.vaultStore.getValue().entities as Record<string, unknown>).length > 0) {
            return;
        }
        await this.fetchSetVaults();
    }

    public async addUserToVault(vaultId: string, userId: string): Promise<boolean> {
        this.setLoading(true);
        let hasSuccessfullyAdded = false;
        try {
            await firstValueFrom(this.apiVaultsService.VaultsAddMember({
                Authorization: await this.authService.getBearer(),
                vaultId,
                creationData: {
                    userId
                }
            }));
            hasSuccessfullyAdded = true;
        } catch (e) {
            console.error(e);
        } finally {
            this.setLoading(false);
        }
        return hasSuccessfullyAdded;
    }

    public setParamVaultId(vaultIdInParams: string | undefined): void {
        this.vaultStore.update({ vaultIdInParams });
    }

    public async getVaultsNotUsedByUser(): Promise<Array<Vault>> {
        return firstValueFrom(this.vaultQuery.vaultsNotUsedByUser$.pipe(switchMap(async vaults => {
            const vaultsNotUsedByUser: Array<Vault> = [];
            for (const vault of vaults) {
                await this.permissionService.fetchVaultPermissions(vault.id);

                if (this.permissionQuery.hasVaultPermission(vault.id, 'VaultsAddMember')) {
                    vaultsNotUsedByUser.push(vault);
                }
            }

            return vaultsNotUsedByUser;
        })));
    }

    public async fetchTagGroupDefinitions(vaultId: string): Promise<void> {
        const tagGroupDefinitions: Record<string, Array<TagGroupDefinitionWithIcon>> = structuredClone(this.vaultStore.getValue().tagGroupDefinitions);
        this.vaultStore.setLoading(true);
        try {
            tagGroupDefinitions[vaultId] = await lastValueFrom(this.apiVaultsService.VaultsGetDocumentTagGroupDefinitions({
                    vaultId,
                    Authorization: await this.authService.getBearer()
                })
                .pipe(map(definitions => {
                    definitions.sort((firstDefinition, secondDefinition) => {
                        return firstDefinition.name.localeCompare(secondDefinition.name);
                    });
                    return definitions
                        .filter(tagGroupDefinition => {
                            return tagGroupDefinition.sourceType === 'UserDefined';
                        })
                        .map(tagGroupDefinition => {
                            return {
                                ...tagGroupDefinition,
                                icon: this.getTagGroupDefinitionIcon(tagGroupDefinition)
                            };
                        });
                })));
            this.vaultStore.update({ tagGroupDefinitions });
        } catch (err) {
            this.snackbarService.showError('ERROR.VAULT_TAG_GROUP_DEFINITIONS', err as Error);
        } finally {
            this.vaultStore.setLoading(false);
        }
    }

    public async initVaults(): Promise<void> {
        await this.fetchVaultsIfEmpty();
    }

    public async fetchViewData(): Promise<VaultList> {
        const token = await this.authService.getToken();
        return new Promise((resolve, reject) => {
            lastValueFrom(this.bfaVaultService.vaultGetList(undefined, new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token)))
                .then(result => {
                    if (result?.items) {
                        resolve(result);
                    } else {
                        reject(new Error(`Invalid response: ${JSON.stringify(result)}`));
                    }
                })
                .catch((error: Error) => {
                    reject(error);
                });
        });
    }

    public async deleteVault(vaultId: string, vaultName: string): Promise<void> {
        let isDeleted = false;
        try {
            await firstValueFrom(this.apiVaultsService.VaultsDelete({
                vaultId,
                Authorization: await this.authService.getBearer(),
            }));
            isDeleted = true;
        } catch (err) {
            console.error(err);
            switch ((err as HttpErrorResponse).status) {
                case 403:
                    this.snackbarService.showError('ERROR.VAULT_DELETE_PERMISSION_MSG');
                    break;
                case 400:
                    this.snackbarService.showError('ERROR.VAULT_DELETE_MSG');
                    break;
                default:
                    this.snackbarService.showError('ERROR.VAULT_DELETE_MSG');
            }
        } finally {
            if (this.systemInfoService.isElectron()) {
                this.checkoutEventService.deleteVaultDirectoryEvent.emit(`${getLocalVaultNameFormat(vaultId, vaultName)}`);
                await firstValueFrom(this.checkoutEventService.deleteVaultDirectoryResultEvent);
            }

            if (isDeleted) {
                this.snackbarService.showInfo('VAULT_FINAL_DELETE_CONFIRM_MESSAGE', {
                    vaultName
                });
                this.unsetActiveVault();

                await this.navigationService.navigateToMe();
            }
        }
    }

    private unsetActiveVault(vaultId?: string): void {
        this.vaultStore.removeActive(vaultId || this.vaultStore.getValue().active);
    }

    private async fetchSetVaults(): Promise<void> {
        this.setHasDataLoaded(false);
        this.vaultStore.setLoading(true);
        try {
            let vaults = await lastValueFrom(this.apiVaultsService.VaultsGetVaults(await this.authService.getBearer()));
            vaults = vaults.filter(vault => vault.state === 'Ready');
            vaults.sort((vaultOne: Vault, vaultTwo: Vault) => {
                return vaultOne.name.localeCompare(vaultTwo.name);
            });
            this.vaultStore.set(vaults);
        } catch (err) {
            this.snackbarService.showError('VAULTS_LOADING_ERROR_MSG', err as Error);
        } finally {
            this.vaultStore.setLoading(false);
            this.setHasDataLoaded(true);
        }
    }

    private setLoading(loading: boolean): void {
        this.vaultStore.setLoading(loading);
    }

    private setHasDataLoaded(hasDataLoaded: boolean): void {
        this.vaultStore.update({ hasDataLoaded });
    }

    private getTagGroupDefinitionIcon(tagGroupDefinition: TagGroupDefinition): Icon {
        const defaultTagGroupDefinition = defaultTagGroupDefinitions.find(defaultTagGroupDefinition => defaultTagGroupDefinition.tagGroupDefinitionId === tagGroupDefinition.id);
        if (defaultTagGroupDefinition) {
            return defaultTagGroupDefinition.icon;
        }

        if (tagGroupDefinition.sourceType === 'UserDefined') {
            return tagGroupDefinition.multi ? ICONS.MULTIPROPERTYGROUP_GREEN : ICONS.PROPGROUP_GREEN;
        }

        return ICONS.PROPGROUP_GREEN;
    }
}
